// src/components/generatePdfFromImages.js
import jsPDF from 'jspdf';
import { getDoc, doc } from 'firebase/firestore';
import { auth, db } from '../firebaseConfig';

const generatePdfFromImages = async (images, projectName = 'Project', captions = {}) => {
  console.log('images array:', images);
  const pdf = new jsPDF({
    orientation: 'portrait',
    unit: 'pt',
    format: 'letter'
  });

  pdf.setProperties({
    title: `${projectName}-Photos`,
    subject: 'Representative Photos',
    creator: 'Vilano Consulting Report Generator'
  });

  const marginX = 72;
  const spacingX = 36;
  const spacingY = 18;
  const imgWidth = 216;
  const imgHeight = 288;
  const captionGap = 12;

  const headerFontSize = 20;
  const headerY = 50;
  const footerMargin = 20;

  let footerHeightPt = 54;
  let logoUrl = '/VilanoLogo.PNG';

  const currentUser = auth.currentUser;

  if (currentUser) {
    try {
      const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
      if (userDoc.exists()) {
        const data = userDoc.data();
        if (data.companyLogoUrl) logoUrl = data.companyLogoUrl;
        if (data.footerHeightPt && !isNaN(data.footerHeightPt)) {
          footerHeightPt = parseInt(data.footerHeightPt, 10);
        }
      }
    } catch (err) {
      console.error('⚠️ Error fetching user settings from Firestore:', err);
    }
  }

  const loadImageElement = async (url) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.src = url;
    await new Promise((resolve, reject) => {
      img.onload = resolve;
      img.onerror = reject;
    });
    return img;
  };

  const loadImageAsBase64 = async (url) => {
    const img = await loadImageElement(url);
    const canvas = document.createElement('canvas');
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(img, 0, 0);
    return canvas.toDataURL('image/png');
  };

  const addHeader = (doc, title) => {
    const fullTitle = `${title} - Representative Photos`;
    const pageWidth = doc.internal.pageSize.getWidth();
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(headerFontSize);
    const textWidth = doc.getTextWidth(fullTitle);
    const centerX = (pageWidth - textWidth) / 2;
    doc.text(fullTitle, centerX, headerY);
  };

  const addFooterLogo = async (doc, logoUrl) => {
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const footerY = pageHeight - footerHeightPt - footerMargin;

    try {
      const img = await loadImageElement(logoUrl);
      const aspectRatio = img.naturalWidth / img.naturalHeight;
      const scaledWidthPt = footerHeightPt * aspectRatio;

      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);
      const highResBase64 = canvas.toDataURL('image/png');

      const logoX = (pageWidth - scaledWidthPt) / 2;
      doc.addImage(highResBase64, 'PNG', logoX, footerY, scaledWidthPt, footerHeightPt);
    } catch (err) {
      console.error('❌ Error rendering footer logo:', err);
    }
  };

  await addHeader(pdf, projectName);
  await addFooterLogo(pdf, logoUrl);

  let count = 0;
  let x = marginX;
  let y = headerY + 20;

  for (let i = 0; i < images.length; i++) {
    const img = images[i];
    const baseName = img.name.replace(/\.[^/.]+$/, '');
    const customCaption = captions[img.name];
    const caption = `PHOTO-${String(i + 1).padStart(2, '0')} ${customCaption || baseName}`;

    try {
      const imgElement = await loadImageElement(img.url);
      const canvas = document.createElement('canvas');
      canvas.width = imgElement.naturalWidth;
      canvas.height = imgElement.naturalHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(imgElement, 0, 0);
      const base64Image = canvas.toDataURL('image/jpeg', 0.85);

      pdf.addImage(base64Image, 'JPEG', x, y, imgWidth, imgHeight);
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.text(caption, x, y + imgHeight + captionGap);

      if (count % 2 === 0) {
        x += imgWidth + spacingX;
      } else {
        x = marginX;
        y += imgHeight + spacingY + captionGap + 4;
      }

      count++;

      if (count % 4 === 0 && i < images.length - 1) {
        pdf.addPage();
        addHeader(pdf, projectName);
        await addFooterLogo(pdf, logoUrl);
        x = marginX;
        y = headerY + 20;
      }
    } catch (err) {
      console.error(`❌ Error rendering "${img.name}":`, err);
    }
  }

  pdf.save(`${projectName}-Photos.pdf`);
};

export default generatePdfFromImages;
